import { CloseOutlined } from '@ant-design/icons';
import { Radio } from 'antd';
import { type KeyboardEvent, useEffect, useState } from 'react';
import styled from 'styled-components';

import { CenterFlex, Flex, Space, VFlex } from '@/components/base';
import Typo from '@/components/Typography/Typo';
import { useToast } from '@/stores/pure/ToastStore';
import { getAlphaColor } from '@/styles/colors/color.utils';
import { animation } from '@/styles/style';
import keymap from '@/utils/keymap';

import type { ImportRuleT, ImportTemplateT } from '../providers/types/types';

type Props = {
  importRule: ImportRuleT;
  template: ImportTemplateT;
  setImportRule: (i: ImportRuleT) => void;
};

const AnalysisTriggerOptions = ({
  template,
  importRule,
  setImportRule,
}: Props) => {
  const addToast = useToast();

  const [includeInput, setIncludeInput] = useState<string>('');
  const [excludeInput, setExcludeInput] = useState<string>('');
  const [includeTags, setIncludeTags] = useState<string[]>(
    importRule.tag === 2 ? importRule.tag_names || [] : [],
  );
  const [excludeTags, setExcludeTags] = useState<string[]>(
    importRule.tag === 3 ? importRule.tag_names || [] : [],
  );

  useEffect(() => {
    if (importRule.tag === 2) {
      setImportRule({ ...importRule, tag_names: includeTags });
    } else if (importRule.tag === 3) {
      setImportRule({ ...importRule, tag_names: excludeTags });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importRule.tag, includeTags, excludeTags]);

  const onIncludeTagKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing) return;
    if (!keymap.isEnter(e)) return;
    const value = e.currentTarget.value;
    if (value.trim() === '') return;

    if (includeTags.find((item) => item === value)) {
      addToast('Tag already selected', 'fail');
      return;
    }
    setIncludeInput('');
    const newTags = includeTags.concat(value);
    setIncludeTags((state) => state.concat(value));
    setImportRule({ ...importRule, tag_names: newTags });
  };

  const onExcludeTagKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing) return;
    if (!keymap.isEnter(e)) return;
    const value = e.currentTarget.value;
    if (value.trim() === '') return;

    if (excludeTags.find((item) => item === value)) {
      addToast('Tag already selected', 'fail');
      return;
    }
    setExcludeInput('');
    const newTags = excludeTags.concat(value);
    setExcludeTags(newTags);
    setImportRule({ ...importRule, tag_names: newTags });
  };

  return (
    <VFlex g={32}>
      {template.status.enable && (
        <VFlex>
          <VFlex g={8}>
            <Typo type="label2" weight="sb">
              Ticket status
            </Typo>
            <Typo>Import when status changes to</Typo>
          </VFlex>
          <Space h={16} />
          <Radio.Group
            value={importRule.status}
            onChange={(e) =>
              setImportRule({ ...importRule, status: +e.target.value })
            }
          >
            <Flex g={16}>
              <Radio value={template.status.solved[0]}>
                <Flex g={16} a="center">
                  <Typo type="label2">{template.status.solved[1]}</Typo>
                  <Default a="center" j="center">
                    default
                  </Default>
                </Flex>
              </Radio>
              <Radio value={template.status.pending[0]}>
                {template.status.pending[1]}
              </Radio>
              <Radio value={template.status.sovledOrPending[0]}>
                {template.status.sovledOrPending[1]}
              </Radio>
            </Flex>
          </Radio.Group>
        </VFlex>
      )}
      {template.tag.enable && (
        <VFlex>
          <Typo type="label2" weight="sb">
            Tags
          </Typo>
          <Space h={16} />
          <Radio.Group
            value={importRule.tag}
            onChange={(e) =>
              setImportRule({ ...importRule, tag: +e.target.value })
            }
          >
            <VFlex g={16}>
              <Radio value={template.tag.all[0]}>
                <Flex g={16} a="center">
                  <Typo type="label2">{template.tag.all[1]}</Typo>
                  <Default a="center" j="center">
                    default
                  </Default>
                </Flex>
              </Radio>
              <Radio value={template.tag.import[0]}>
                {template.tag.import[1]}
              </Radio>
              {importRule.tag === 2 && (
                <InputWrapper a="center">
                  {includeTags.map((tag) => (
                    <SelectItem
                      key={tag}
                      item={tag}
                      onDeleteClick={(text) => {
                        setIncludeTags((state) =>
                          state.filter((t) => t != text),
                        );
                      }}
                    />
                  ))}
                  <input
                    placeholder="tags separated with enter"
                    value={includeInput}
                    onChange={(e) => setIncludeInput(e.currentTarget.value)}
                    onKeyDown={onIncludeTagKeyDown}
                  />
                </InputWrapper>
              )}
              <Radio value={template.tag.doNotImport[0]}>
                {template.tag.doNotImport[1]}
              </Radio>
              {importRule.tag === 3 && (
                <InputWrapper a="center">
                  {excludeTags.map((tag) => (
                    <SelectItem
                      key={tag}
                      item={tag}
                      onDeleteClick={(text) => {
                        setExcludeTags((state) =>
                          state.filter((t) => t != text),
                        );
                      }}
                    />
                  ))}
                  <input
                    placeholder="tags separated with enter"
                    value={excludeInput}
                    onChange={(e) => setExcludeInput(e.currentTarget.value)}
                    onKeyDown={onExcludeTagKeyDown}
                  />
                </InputWrapper>
              )}
            </VFlex>
          </Radio.Group>
        </VFlex>
      )}
    </VFlex>
  );
};

export default AnalysisTriggerOptions;

const Default = styled(Flex)`
  border-radius: 4px;
  height: 22px;
  width: 51px;
  background-color: ${(props) =>
    getAlphaColor(props.theme.colors.primary100, 0.05)};
  color: ${(props) => props.theme.colors.primary100};
  font-size: 12px;
`;

const InputWrapper = styled(Flex)`
  max-height: 120px;
  overflow: auto;
  border-radius: 8px;
  padding: 8px;
  border: 1px solid ${(props) => props.theme.colors.gray300};
  flex-wrap: wrap;
  gap: 10px;
  input {
    background-color: transparent;
    border: none;
    outline: none;
    flex: 1;
    min-width: 200px;
    font-size: 14px;
    color: ${(props) => props.theme.colors.gray800};
    &::placeholder {
      color: ${(props) => props.theme.colors.gray500};
    }
  }
`;

type ItemStyleProps = {
  item: string;
  onDeleteClick: (text: string) => void;
};
const SelectItem = ({ item, onDeleteClick }: ItemStyleProps) => {
  return (
    <SelectItemStyle>
      <Typo type="label3" style={{ transition: 'all 0.2s' }}>
        {item}
      </Typo>
      <DeleteIconWrapper onClick={() => onDeleteClick(item)}>
        <CloseOutlined />
      </DeleteIconWrapper>
    </SelectItemStyle>
  );
};

const DeleteIconWrapper = styled(CenterFlex)`
  background-color: ${(props) => props.theme.colors.FL100};
  color: ${(props) => props.theme.colors.FD80};
  padding: 4px;
  border-radius: 16px;
  font-size: 8px;
  cursor: pointer;
`;

const SelectItemStyle = styled(Flex)`
  width: fit-content;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 30px;
  background-color: ${({ theme }) => theme.colors.gray200};
  cursor: unset;
  font-weight: 600;
  transition: all 0.2s;
  &:hover {
    background-color: ${({ theme }) => theme.colors.gray300};
    filter: brightness(1.05);
  }
  animation: ${animation.scaleIn} 0.4s both;
`;
